import { useState, useCallback, useMemo } from "react";
import { pushToDataLayer } from "../utils/tracking";
import { buildExportProposalDataLayerEvent } from "../utils/projectExportTracking";
import Project from "../../project/models/Project";
import { FeedbackToastProps } from "../components/FeedbackToast";
import { calculateRemainingMowerCapacity } from "../../project/coverageCalculations";
import { renderMapImage } from "husqvarnamap";
import { getMapDataFromProject, MAP_CONFIG } from "../utils";

const useProjectExport = (
  project?: Project,
  onShowToast?: (toast: FeedbackToastProps) => void
) => {
  const [isExporting, setIsExporting] = useState(false);
  const getToastText = useCallback(
    (success: boolean) => {
      return success
        ? `Successfully exported presentation for ${project?.name}`
        : `Failed to export presentation for ${project?.name}`;
    },
    [project?.name]
  );


  const trackExport = useCallback(() => {
    if (!project) return;

    pushToDataLayer(
      buildExportProposalDataLayerEvent(project, {
        documentname: `Presentation ${project.name}.pptx`,
        documenttype: "pptx",
      })
    );
  }, [project]);

  const coveragePerProduct = useMemo(() => {
  if (!project?.proposal?.mowers?.length) return [];

  return project.proposal.mowers.map((product) => {
    const connectedAreas = project.areas.filter((a) =>
      product.areaIds.includes(a.id)
    );

    const firstOrDefaultConnection = connectedAreas.find((a) =>
      a.proposalProductConnections.find(
        (conn) => conn.proposalProduct !== null && conn.proposalProduct.id === product.id
      )
    )?.proposalProductConnections?.[0];

    const possibleToMowOnDifferentHours =
      firstOrDefaultConnection?.possibleToMowOnDifferentHours ?? true;

    return {
      product: product.id,
      productName: product.product.name,
      coverage: calculateRemainingMowerCapacity(
        product.product,
        product.quantity,
        connectedAreas,
        possibleToMowOnDifferentHours,
        product
      ),
    };
  });
}, [project?.proposal?.mowers, project?.areas]);

  const coverageDictionary = useCallback(() => {
    return Object.fromEntries(
      coveragePerProduct.map((x) => [x.product, x.coverage])
    );
  }, [project]);

  const downloadFile = useCallback(
    (blob: Blob) => {
      if (!blob || !project) return;

      const a = document.createElement("a");
      a.href = URL.createObjectURL(
        new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        })
      );
      a.setAttribute("download", `Presentation ${project.name}.pptx`);
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(a.href); // Clean up the URL object
      document.body.removeChild(a);
    },
    [project]
  );

  const handleExportResponse = useCallback(
    async (response: Response) => {
      if (!response.ok) {
        onShowToast?.({
          show: true,
          success: false,
          text: getToastText(false),
        });
        return null;
      }

      try {
        const blob = await response.blob();
        downloadFile(blob);
        onShowToast?.({
          show: true,
          success: true,
          text: getToastText(true),
        });
        trackExport();
        return blob;
      } catch (error) {
        console.error("Error processing export response:", error);
        onShowToast?.({
          show: true,
          success: false,
          text: getToastText(false),
        });
        return null;
      }
    },
    [downloadFile, getToastText, onShowToast]
  );

  const exportProject = useCallback(async () => {
    if (!project) return;

    setIsExporting(true);
    try {
      let mapImageBase64 = '';
      try {
        const mapData = getMapDataFromProject(project);
        const mapImageDataUrl = await renderMapImage({ accessToken: MAP_CONFIG.accessToken, mapData: mapData as object, format: 'png' });
        mapImageBase64 = mapImageDataUrl.replace(/^data:image\/\w+;base64,/, '');
      } catch (e) {
        console.warn("Failed to render map image, exporting without it:", e);
      }

      const exportModel = {
        projectId: project.id,
        useDepreciation: true,
        machineCoverage: coverageDictionary(),
        mapImage: mapImageBase64 || undefined,
      };

      const response = await fetch(`/api/projects/${project.id}/export-ppt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(exportModel),
      });

      await handleExportResponse(response);
    } catch (error) {
      console.error("Export failed:", error);
      onShowToast?.({
        show: true,
        success: false,
        text: getToastText(false),
      });
    } finally {
      setIsExporting(false);
    }
  }, [project, trackExport, coverageDictionary, handleExportResponse, onShowToast, getToastText]);

    return {
      isExporting,
      exportProject,
    };
};

export default useProjectExport;
